// Calm pronunciation playback via the browser's built-in Web Speech API.
// No audio files, no network, no autoplay — a learner taps a quiet speaker
// icon to hear a word, verb, or example sentence spoken in Italian.
//
// Design notes:
//  - We pick an Italian ("it-*") voice when the platform offers one, preferring
//    standard Italian (it-IT) then any Italian variant, then falling back to the
//    browser default with the lang hint set so it still sounds Italian-ish.
//  - Voices load asynchronously on some browsers, so we listen for
//    `voiceschanged` and re-resolve.
//  - speechSynthesis is cancelled before each utterance so rapid taps don't
//    queue up a backlog.

let _voices = [];
let _preferred = null;

function refreshVoices() {
  if (typeof speechSynthesis === "undefined") return;
  _voices = speechSynthesis.getVoices() || [];
  _preferred =
    _voices.find((v) => /^it-IT/i.test(v.lang)) ||
    _voices.find((v) => /^it[-_]/i.test(v.lang)) ||
    _voices.find((v) => /^it$/i.test(v.lang)) ||
    null;
}

if (typeof speechSynthesis !== "undefined") {
  refreshVoices();
  // Chrome/Safari populate the list lazily.
  speechSynthesis.addEventListener?.("voiceschanged", refreshVoices);
}

// True when the platform can speak at all — used to hide buttons gracefully
// on the rare browser without speech synthesis.
export function audioSupported() {
  return typeof speechSynthesis !== "undefined" && typeof SpeechSynthesisUtterance !== "undefined";
}

// Speak an Italian string. Safe to call repeatedly; cancels any in-flight speech.
export function speak(text, lang = "it-IT") {
  if (!audioSupported() || !text) return;
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.lang = lang;
    if (_preferred) u.voice = _preferred;
    u.rate = 0.92; // a touch slower than native — kinder to A1/A2 ears
    u.pitch = 1;
    speechSynthesis.speak(u);
  } catch {
    /* speech is a nicety; never let it throw into the UI */
  }
}

// Returns inline HTML for a calm speaker button. The phrase is carried on a
// data attribute and handled by the single delegated listener in main.js, so
// any view can drop a button in markup without wiring its own handler.
//   size: "sm" (inline beside text) | "lg" (detail headers)
export function speakButtonHTML(text, { size = "sm", label = "Listen" } = {}) {
  if (!audioSupported() || !text) return "";
  const enc = encodeURIComponent(String(text));
  const cls =
    size === "lg"
      ? "speak-btn inline-flex items-center justify-center w-9 h-9 rounded-full text-sage-500 hover:text-sage-700 hover:bg-sage-50 transition"
      : "speak-btn inline-flex items-center justify-center w-6 h-6 rounded-full text-sage-400 hover:text-sage-600 hover:bg-sage-50 transition align-middle";
  const icon = size === "lg" ? "text-lg" : "text-sm";
  return `<button type="button" class="${cls}" data-speak="${enc}" aria-label="${label} — Italian pronunciation" title="${label}"><span class="${icon}" aria-hidden="true">🔊</span></button>`;
}

// Wire a single document-level listener that plays the phrase on any
// [data-speak] element. Call once at startup.
export function initAudioDelegation(rootEl = document) {
  rootEl.addEventListener("click", (e) => {
    const btn = e.target.closest?.("[data-speak]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const text = decodeURIComponent(btn.getAttribute("data-speak") || "");
    speak(text);
  });
}
